// @flow
import { toastError } from 'bench-internal-theme';
import { Mutation, Query } from '@apollo/client/react/components';
import React from 'react';
import { WrappedSpinner } from 'spinner';

import AssignmentsTable from './AssignmentsTable';
import type { AssignedPerson } from './types';
import {
  DELETE_PERSON_CLIENT_ROLES,
  GET_ASSIGNED_PERSONS,
  GET_CLIENT_ROLES,
  UPDATE_PERSON_CLIENT_ROLES,
} from './queries';
import mapClientRolesToOptions from './mapClientRolesToOptions';
import openCreateOrEditPersonModal from './openCreateOrEditPersonModal';

const handleDeletePersonClientRolesError = () =>
  toastError(
    'Unable to remove assignment',
    'An error occurred when trying to remove the assignment. It might work if you give it another try.',
  );

const handleUpdatePersonClientRolesError = () =>
  toastError(
    'Unable to update client role',
    'An error occurred when trying to update the assigned client role. It might work if you give it another try.',
  );

type AssignmentsTableContainerProps = {
  assignedPersons: Array<AssignedPerson>,
  clientId: string,
};

const AssignmentsTableContainer = ({
  assignedPersons,
  clientId,
}: AssignmentsTableContainerProps) => (
  <Mutation
    mutation={DELETE_PERSON_CLIENT_ROLES}
    onError={handleDeletePersonClientRolesError}
    refetchQueries={[{ query: GET_ASSIGNED_PERSONS, variables: { clientId } }]}
  >
    {deletePersonClientRoles => (
      <Mutation
        mutation={UPDATE_PERSON_CLIENT_ROLES}
        onError={handleUpdatePersonClientRolesError}
        refetchQueries={[{ query: GET_ASSIGNED_PERSONS, variables: { clientId } }]}
      >
        {updatePersonClientRoles => (
          <Query query={GET_CLIENT_ROLES}>
            {(getClientRoles: any) => {
              if (getClientRoles.error) {
                return `An error occurred while trying to fetch the list of client roles. The error was:${getClientRoles.error.message}`;
              }
              if (getClientRoles.loading) {
                return <WrappedSpinner />;
              }
              const clientRoleOptions = mapClientRolesToOptions(getClientRoles.data.clientRoles);
              const openEditPersonModal = person => openCreateOrEditPersonModal(clientId, person);
              const unassignPersonFromClient = personId => {
                deletePersonClientRoles({
                  variables: {
                    clientId,
                    personId,
                  },
                });
              };
              const updatePersonClientRolesForPerson = (personId, clientRoleIds) => {
                updatePersonClientRoles({
                  variables: {
                    clientId,
                    personId,
                    clientRoleIds,
                  },
                });
              };
              return (
                <AssignmentsTable
                  assignedPersons={assignedPersons}
                  clientRoleOptions={clientRoleOptions}
                  onRequestEditPerson={openEditPersonModal}
                  onRequestRemovePerson={unassignPersonFromClient}
                  updatePersonClientRoles={updatePersonClientRolesForPerson}
                />
              );
            }}
          </Query>
        )}
      </Mutation>
    )}
  </Mutation>
);

export default AssignmentsTableContainer;
